/* External Deps */
import { FC, ReactElement, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tooltip,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from "@mui/material";
import {
  ExpandMore,
  ChevronRight,
  FolderOpen,
  Folder,
  Description,
  Download,
  VerifiedUser,
  Warning,
  Error as ErrorIcon,
  Info,
  Close,
} from "@mui/icons-material";
import DeleteIcon from "@/assets/svgs/garbage.svg";

/* Internal Deps */
import { IconImage, PageHeader } from "@/components";
import UploadCustomsDocuments from "../../../upload-customs-documents";
import {
  HeaderRightNode,
  HeaderSearchBox,
  StyledContainedButton,
  StyledTextInput,
} from "../../../styles";
import {
  useGetCustomsClustersQuery,
  useGetCustomsDocumentsQuery,
  useDeleteCustomsDocumentMutation,
  useLazyGetCustomsDocumentQuery,
  useRunClusterAuditMutation,
  useLazyGetClusterAuditQuery,
} from "@/services";
import { useDebounce, useSnackbar, useDocumentProcessingPolling } from "@/hooks";
import { search_img, upload_img } from "@/constants";
import { useAppSelector } from "@/store";
import { DocumentAccessLevel, roles } from "@/roles";

// Document type labels
const getDocumentTypeLabel = (type?: CustomsDocumentType) => {
  if (!type) return "Unknown";
  switch (type) {
    case "ENTRY_SUMMARY_7501":
      return "Entry Summary (7501)";
    case "COMMERCIAL_INVOICE":
      return "Commercial Invoice";
    case "BILL_OF_LADING":
      return "Bill of Lading";
    case "PACKING_LIST":
      return "Packing List";
    case "ARRIVAL_NOTICE":
      return "Arrival Notice";
    case "ISF":
      return "ISF";
    case "OTHER":
      return "Other";
    default:
      return type;
  }
};

const getStatusLabel = (status: CustomsDocumentStatus) => {
  switch (status) {
    case "READY":
      return "Ready";
    case "FAILED":
      return "Failed";
    case "UPLOADED":
      return "Uploaded";
    case "TEXT_EXTRACTED":
      return "Processing...";
    case "KEYS_EXTRACTED":
      return "Clustering...";
    default:
      return status;
  }
};

// Identifier chip style (plain, neutral)
const identifierChipStyle = {
  bg: "#F3F4F6",
  color: "#374151",
};

// Get readable label for identifier type
const getIdentifierTypeLabel = (type: string) => {
  switch (type) {
    case "ENTRY":
      return "Entry";
    case "BL":
      return "BL";
    case "CONTAINER":
      return "Container";
    case "PO":
      return "PO";
    case "INV":
      return "Invoice";
    default:
      return type;
  }
};

// Audit status helpers
const getAuditStatusColor = (status: ClusterAuditStatus) => {
  switch (status) {
    case "APPROVED":
      return "success";
    case "NEEDS_REVIEW":
      return "warning";
    case "DISCREPANCIES_FOUND":
      return "error";
    default:
      return "default";
  }
};

const getAuditStatusLabel = (status: ClusterAuditStatus) => {
  switch (status) {
    case "APPROVED":
      return "Approved";
    case "NEEDS_REVIEW":
      return "Needs Review";
    case "DISCREPANCIES_FOUND":
      return "Discrepancies Found";
    default:
      return status;
  }
};

const getSeverityColor = (severity: DiscrepancySeverity) => {
  switch (severity) {
    case "HIGH":
      return "#DC2626";
    case "MEDIUM":
      return "#D97706";
    case "LOW":
      return "#2563EB";
    case "INFO":
      return "#6B7280";
    default:
      return "#6B7280";
  }
};

const getSeverityIcon = (severity: DiscrepancySeverity) => {
  switch (severity) {
    case "HIGH":
      return <ErrorIcon sx={{ fontSize: 18, color: "#DC2626" }} />;
    case "MEDIUM":
      return <Warning sx={{ fontSize: 18, color: "#D97706" }} />;
    case "LOW":
      return <Info sx={{ fontSize: 18, color: "#2563EB" }} />;
    case "INFO":
      return <Info sx={{ fontSize: 18, color: "#6B7280" }} />;
    default:
      return <Info sx={{ fontSize: 18 }} />;
  }
};

const getCategoryLabel = (category: DiscrepancyCategory) => {
  switch (category) {
    case "VALUE_MISMATCH":
      return "Value Mismatch";
    case "QUANTITY_MISMATCH":
      return "Quantity Mismatch";
    case "HTS_CLASSIFICATION":
      return "HTS Classification";
    case "COUNTRY_OF_ORIGIN":
      return "Country of Origin";
    case "MANUFACTURER_MISMATCH":
      return "Manufacturer Mismatch";
    case "MISSING_DOCUMENT":
      return "Missing Document";
    case "DATE_DISCREPANCY":
      return "Date Discrepancy";
    case "IDENTIFIER_MISMATCH":
      return "Identifier Mismatch";
    case "DUTY_CALCULATION":
      return "Duty Calculation";
    case "OTHER":
      return "Other";
    default:
      return category;
  }
};

interface ClusterRowProps {
  cluster: CustomsCluster;
  documents: CustomsDocument[];
  isExpanded: boolean;
  onToggle: () => void;
  onDownload: (doc: CustomsDocument) => void;
  onDelete: (doc: CustomsDocument) => void;
  loadingStates: { delete: string[]; download: string[]; audit: string[] };
  onEntryClick: (entryNumber: string) => void;
  onTypeClick: (type: CustomsDocumentType) => void;
  onIdentifierClick: (type: string, value: string) => void;
  onRunAudit: (clusterId: string) => void;
  audit: ClusterAudit | null;
  onViewAudit: (audit: ClusterAudit) => void;
}

const ClusterRow: FC<ClusterRowProps> = ({
  cluster,
  documents,
  isExpanded,
  onToggle,
  onDownload,
  onDelete,
  loadingStates,
  onEntryClick,
  onTypeClick,
  onIdentifierClick,
  onRunAudit,
  audit,
  onViewAudit,
}) => {
  const entryNumber = cluster.entryNumbers?.[0] || "No Entry Number";
  const isAuditing = loadingStates.audit.includes(cluster._id);

  // Group identifiers by type for consolidated display (with deduplication and filtering)
  const identifiersByType = useMemo(() => {
    const grouped: Record<string, string[]> = {};
    const seen = new Set<string>();

    // Garbage values to filter out
    const invalidValues = new Set([
      "UNDEFINED",
      "NO",
      "TERM",
      "NULL",
      "N/A",
      "NA",
      "NONE",
      "",
    ]);

    cluster.clusterKeys?.forEach((key) => {
      const value = key.value?.trim();
      if (!value) return;

      // Skip if contains UNDEFINED or other garbage patterns
      const upperValue = value.toUpperCase();
      if (
        invalidValues.has(upperValue) ||
        upperValue.includes("UNDEFINED") ||
        upperValue.includes("NULL")
      ) {
        return;
      }

      // Skip very short values (likely extraction errors) except for certain types
      if (value.length < 3 && key.type !== "PO") {
        return;
      }

      // Create a dedup key (type + normalized value)
      const dedupKey = `${key.type}:${upperValue.replace(/[-\s]/g, "")}`;
      if (seen.has(dedupKey)) return;
      seen.add(dedupKey);

      if (!grouped[key.type]) grouped[key.type] = [];
      grouped[key.type].push(value);
    });

    return grouped;
  }, [cluster.clusterKeys]);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        border: "1px solid #E5E7EB",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {/* Cluster Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 1.5,
          bgcolor: isExpanded ? "#F9FAFB" : "white",
          cursor: "pointer",
          "&:hover": { bgcolor: "#F9FAFB" },
        }}
        onClick={onToggle}
      >
        <IconButton size="small" sx={{ mr: 1 }}>
          {isExpanded ? (
            <ExpandMore sx={{ color: "#6B7280" }} />
          ) : (
            <ChevronRight sx={{ color: "#6B7280" }} />
          )}
        </IconButton>

        {isExpanded ? (
          <FolderOpen sx={{ color: "#F59E0B", mr: 1.5 }} />
        ) : (
          <Folder sx={{ color: "#F59E0B", mr: 1.5 }} />
        )}

        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: "#030712",
              cursor: "pointer",
              "&:hover": { color: "#0B494B", textDecoration: "underline" },
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (cluster.entryNumbers?.[0]) {
                onEntryClick(cluster.entryNumbers[0]);
              }
            }}
          >
            Entry: {entryNumber}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.75, mt: 0.75, flexWrap: "wrap" }}>
            {Object.entries(identifiersByType).map(([type, values]) => {
              const typeLabel = getIdentifierTypeLabel(type);
              const label =
                values.length === 1
                  ? `${typeLabel}: ${values[0]}`
                  : `${values.length} ${typeLabel}#s`;

              return (
                <Tooltip
                  key={type}
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography
                        sx={{ fontWeight: 600, mb: 0.5, fontSize: "12px" }}
                      >
                        {typeLabel} Numbers
                      </Typography>
                      {values.map((val, i) => (
                        <Typography key={i} sx={{ fontSize: "12px" }}>
                          {val}
                        </Typography>
                      ))}
                    </Box>
                  }
                  arrow
                  slotProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "#1e293b",
                        color: "white",
                        fontSize: "0.875rem",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                      },
                    },
                  }}
                >
                  <Chip
                    label={label}
                    size="small"
                    sx={{
                      fontSize: "12px",
                      fontWeight: 500,
                      height: "24px",
                      bgcolor: identifierChipStyle.bg,
                      color: identifierChipStyle.color,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "#E5E7EB" },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onIdentifierClick(type, values[0]);
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        </Box>

        {/* Audit Status Badge */}
        {audit && (
          <Tooltip title={`Click to view audit details`} arrow>
            <Chip
              icon={
                audit.status === "APPROVED" ? (
                  <VerifiedUser sx={{ fontSize: 14 }} />
                ) : audit.status === "NEEDS_REVIEW" ? (
                  <Warning sx={{ fontSize: 14 }} />
                ) : (
                  <ErrorIcon sx={{ fontSize: 14 }} />
                )
              }
              label={getAuditStatusLabel(audit.status)}
              size="small"
              color={getAuditStatusColor(audit.status)}
              sx={{
                fontSize: "10px",
                mr: 1,
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                onViewAudit(audit);
              }}
            />
          </Tooltip>
        )}

        {/* Run Audit Button */}
        <Button
          size="small"
          variant="outlined"
          startIcon={
            isAuditing ? (
              <CircularProgress size={14} />
            ) : (
              <VerifiedUser sx={{ fontSize: 16 }} />
            )
          }
          disabled={isAuditing || documents.length === 0}
          onClick={(e) => {
            e.stopPropagation();
            onRunAudit(cluster._id);
          }}
          sx={{
            fontSize: "11px",
            mr: 1,
            textTransform: "none",
            borderColor: "#0B494B",
            color: "#0B494B",
            "&:hover": { borderColor: "#0B494B", bgcolor: "#F0FDFA" },
          }}
        >
          {isAuditing ? "Auditing..." : audit ? "Re-audit" : "Run Audit"}
        </Button>

        <Chip
          label={`${documents.length} document${documents.length !== 1 ? "s" : ""}`}
          size="small"
          sx={{
            bgcolor: "#F3F4F6",
            color: "#374151",
            fontWeight: 500,
            fontSize: "11px",
          }}
        />
      </Box>

      {/* Expanded Documents List */}
      <Collapse in={isExpanded}>
        <Box sx={{ borderTop: "1px solid #E5E7EB" }}>
          {documents.map((doc) => (
            <Box
              key={doc._id}
              sx={{
                display: "flex",
                alignItems: "center",
                p: 1.5,
                pl: 7,
                borderBottom: "1px solid #F3F4F6",
                "&:last-child": { borderBottom: "none" },
                "&:hover": { bgcolor: "#FAFAFA" },
              }}
            >
              <Description sx={{ color: "#9CA3AF", mr: 1.5, fontSize: 20 }} />

              {/* Filename */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Tooltip title={doc.filename} arrow>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "12px",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "250px",
                    }}
                  >
                    {doc.filename}
                  </Typography>
                </Tooltip>
              </Box>

              {/* Document Type (plain text) */}
              <Typography
                variant="body2"
                sx={{
                  fontSize: "12px",
                  color: "#6B7280",
                  mr: 2,
                  cursor: "pointer",
                  "&:hover": { color: "#374151" },
                }}
                onClick={() => doc.documentType && onTypeClick(doc.documentType)}
              >
                {getDocumentTypeLabel(doc.documentType)}
              </Typography>

              {/* Status - only show if NOT ready */}
              {doc.status !== "READY" && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "12px",
                    color: doc.status === "FAILED" ? "#DC2626" : "#6B7280",
                    mr: 2,
                  }}
                >
                  {getStatusLabel(doc.status)}
                </Typography>
              )}

              {/* Actions */}
              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                {(doc.status === "READY" ||
                  doc.status === "TEXT_EXTRACTED" ||
                  doc.status === "KEYS_EXTRACTED") && (
                  <Tooltip title="Download">
                    <IconButton
                      size="small"
                      onClick={() => onDownload(doc)}
                      disabled={loadingStates.download.includes(doc._id)}
                      sx={{ color: "#171717" }}
                    >
                      {loadingStates.download.includes(doc._id) ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Download sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={() => onDelete(doc)}
                    disabled={loadingStates.delete.includes(doc._id)}
                    sx={{ color: "#171717" }}
                  >
                    {loadingStates.delete.includes(doc._id) ? (
                      <CircularProgress size={16} />
                    ) : (
                      <img src={DeleteIcon} alt="Delete" style={{ width: 18, height: 18 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

// Unclustered documents row
interface UnclusteredRowProps {
  documents: CustomsDocument[];
  isExpanded: boolean;
  onToggle: () => void;
  onDownload: (doc: CustomsDocument) => void;
  onDelete: (doc: CustomsDocument) => void;
  loadingStates: { delete: string[]; download: string[] };
  onTypeClick: (type: CustomsDocumentType) => void;
}

const UnclusteredRow: FC<UnclusteredRowProps> = ({
  documents,
  isExpanded,
  onToggle,
  onDownload,
  onDelete,
  loadingStates,
  onTypeClick,
}) => {
  if (documents.length === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        border: "1px solid #E5E7EB",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 1.5,
          bgcolor: isExpanded ? "#FEF3C7" : "#FFFBEB",
          cursor: "pointer",
          "&:hover": { bgcolor: "#FEF3C7" },
        }}
        onClick={onToggle}
      >
        <IconButton size="small" sx={{ mr: 1 }}>
          {isExpanded ? (
            <ExpandMore sx={{ color: "#6B7280" }} />
          ) : (
            <ChevronRight sx={{ color: "#6B7280" }} />
          )}
        </IconButton>

        {isExpanded ? (
          <FolderOpen sx={{ color: "#D97706", mr: 1.5 }} />
        ) : (
          <Folder sx={{ color: "#D97706", mr: 1.5 }} />
        )}

        <Box sx={{ flex: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: "#92400E" }}>
            Unclustered Documents
          </Typography>
          <Typography variant="caption" sx={{ color: "#B45309" }}>
            Documents without entry numbers or pending clustering
          </Typography>
        </Box>

        <Chip
          label={`${documents.length} document${documents.length !== 1 ? "s" : ""}`}
          size="small"
          sx={{
            bgcolor: "#FDE68A",
            color: "#92400E",
            fontWeight: 500,
            fontSize: "11px",
          }}
        />
      </Box>

      {/* Documents */}
      <Collapse in={isExpanded}>
        <Box sx={{ borderTop: "1px solid #FDE68A" }}>
          {documents.map((doc) => (
            <Box
              key={doc._id}
              sx={{
                display: "flex",
                alignItems: "center",
                p: 1.5,
                pl: 7,
                borderBottom: "1px solid #F3F4F6",
                "&:last-child": { borderBottom: "none" },
                "&:hover": { bgcolor: "#FAFAFA" },
              }}
            >
              <Description sx={{ color: "#9CA3AF", mr: 1.5, fontSize: 20 }} />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Tooltip title={doc.filename} arrow>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "12px",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "250px",
                    }}
                  >
                    {doc.filename}
                  </Typography>
                </Tooltip>
              </Box>

              {/* Document Type (plain text) */}
              <Typography
                variant="body2"
                sx={{
                  fontSize: "12px",
                  color: "#6B7280",
                  mr: 2,
                  cursor: "pointer",
                  "&:hover": { color: "#374151" },
                }}
                onClick={() => doc.documentType && onTypeClick(doc.documentType)}
              >
                {getDocumentTypeLabel(doc.documentType)}
              </Typography>

              {/* Status - only show if NOT ready */}
              {doc.status !== "READY" && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "12px",
                    color: doc.status === "FAILED" ? "#DC2626" : "#6B7280",
                    mr: 2,
                  }}
                >
                  {getStatusLabel(doc.status)}
                </Typography>
              )}

              {/* Actions */}
              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                {(doc.status === "READY" ||
                  doc.status === "TEXT_EXTRACTED" ||
                  doc.status === "KEYS_EXTRACTED") && (
                  <Tooltip title="Download">
                    <IconButton
                      size="small"
                      onClick={() => onDownload(doc)}
                      disabled={loadingStates.download.includes(doc._id)}
                      sx={{ color: "#171717" }}
                    >
                      {loadingStates.download.includes(doc._id) ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Download sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={() => onDelete(doc)}
                    disabled={loadingStates.delete.includes(doc._id)}
                    sx={{ color: "#171717" }}
                  >
                    {loadingStates.delete.includes(doc._id) ? (
                      <CircularProgress size={16} />
                    ) : (
                      <img src={DeleteIcon} alt="Delete" style={{ width: 18, height: 18 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

const EntrySummary: FC = (): ReactElement => {
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set());
  const [showUnclustered, setShowUnclustered] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filterType, setFilterType] = useState<CustomsDocumentType | null>(null);
  const [loadingStates, setLoadingStates] = useState<{ delete: string[]; download: string[]; audit: string[] }>({
    delete: [],
    download: [],
    audit: [],
  });
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [auditResults, setAuditResults] = useState<Record<string, ClusterAudit>>({});
  const [selectedAudit, setSelectedAudit] = useState<ClusterAudit | null>(null);
  const [showAuditModal, setShowAuditModal] = useState(false);

  const { show } = useSnackbar();
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const { user } = useAppSelector((state) => state.auth);
  const [deleteCustomsDocument] = useDeleteCustomsDocumentMutation();
  const [getCustomsDocument] = useLazyGetCustomsDocumentQuery();
  const [runClusterAudit] = useRunClusterAuditMutation();
  const [getClusterAudit] = useLazyGetClusterAuditQuery();

  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const isTDAdmin = useMemo(() => {
    return user?.userRole === roles.TDADMIN;
  }, [user]);

  const shouldSkip = useMemo(
    () => {
      if (isTDAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT") {
        return false;
      }
      return canSelectClients && !selectedClient;
    },
    [canSelectClients, selectedClient, selectedTenant, isTDAdmin]
  );

  // Fetch clusters
  const {
    data: clustersData,
    isLoading: clustersLoading,
    refetch: refetchClusters,
  } = useGetCustomsClustersQuery(
    {
      pageSize: 100,
      pageNo: 1,
      clientId: selectedClient?._id,
      identifier: searchText || undefined,
      ...(isTDAdmin && selectedTenant && { tenantId: selectedTenant._id }),
    },
    {
      refetchOnMountOrArgChange: true,
      skip: shouldSkip,
    }
  );

  // Fetch all documents for the clusters
  const {
    data: documentsData,
    isLoading: documentsLoading,
    refetch: refetchDocuments,
  } = useGetCustomsDocumentsQuery(
    {
      pageSize: 500,
      pageNo: 1,
      clientId: selectedClient?._id,
      ...(isTDAdmin && selectedTenant && { tenantId: selectedTenant._id }),
    },
    {
      refetchOnMountOrArgChange: true,
      skip: shouldSkip,
    }
  );

  // Poll for document processing status updates
  const { isPolling, processingCounts } = useDocumentProcessingPolling({
    documents: documentsData?.data,
    onRefetch: () => {
      refetchDocuments();
      refetchClusters();
    },
    pollingInterval: 3000,
    enabled: !shouldSkip,
  });

  const canUploadDocs = useMemo(
    () =>
      user?.userRole !== roles.SPCLIENT ||
      user?.clientPermission?.documents === DocumentAccessLevel.Admin,
    [user]
  );

  // Group documents by cluster
  const { clusteredDocs, unclusteredDocs } = useMemo(() => {
    const allDocs = documentsData?.data || [];
    const clusters = clustersData?.data || [];

    const clustered: Record<string, CustomsDocument[]> = {};
    const unclustered: CustomsDocument[] = [];

    // Initialize cluster groups
    clusters.forEach((cluster) => {
      clustered[cluster._id] = [];
    });

    // Sort documents into clusters
    allDocs.forEach((doc) => {
      if (doc.clusterId && clustered[doc.clusterId]) {
        clustered[doc.clusterId].push(doc);
      } else {
        // No clusterId OR orphaned clusterId (cluster not in current page) → add to unclustered
        unclustered.push(doc);
      }
    });

    // Apply type filter if set
    if (filterType) {
      Object.keys(clustered).forEach((clusterId) => {
        clustered[clusterId] = clustered[clusterId].filter(
          (doc) => doc.documentType === filterType
        );
      });
      return {
        clusteredDocs: clustered,
        unclusteredDocs: unclustered.filter((doc) => doc.documentType === filterType),
      };
    }

    return { clusteredDocs: clustered, unclusteredDocs: unclustered };
  }, [documentsData, clustersData, filterType]);

  // Toggle cluster expansion
  const toggleCluster = (clusterId: string) => {
    setExpandedClusters((prev) => {
      const next = new Set(prev);
      if (next.has(clusterId)) {
        next.delete(clusterId);
      } else {
        next.add(clusterId);
      }
      return next;
    });
  };

  // Document actions
  const handleDownload = async (doc: CustomsDocument) => {
    setLoadingStates((prev) => ({ ...prev, download: [...prev.download, doc._id] }));
    try {
      const result = await getCustomsDocument({
        docId: doc._id,
        tenantId: isTDAdmin && selectedTenant ? selectedTenant._id : undefined,
      }).unwrap();
      if (result?.downloadUrl) {
        window.open(result.downloadUrl, "_blank");
      }
    } catch (error) {
      show("Download Failed", (error as Error).message, "error");
    }
    setLoadingStates((prev) => ({
      ...prev,
      download: prev.download.filter((id) => id !== doc._id),
    }));
  };

  const handleDelete = async (doc: CustomsDocument) => {
    setLoadingStates((prev) => ({ ...prev, delete: [...prev.delete, doc._id] }));
    try {
      const isDeleted = await deleteCustomsDocument({
        docId: doc._id,
        tenantId: isTDAdmin && selectedTenant ? selectedTenant._id : undefined,
      }).unwrap();
      if (isDeleted) {
        show("Successfully deleted!", `Document "${doc.filename}" deleted successfully!`, "success");
        refetchDocuments();
        refetchClusters();
      }
    } catch (error) {
      show("Delete Failed", (error as Error).message, "error");
    }
    setLoadingStates((prev) => ({
      ...prev,
      delete: prev.delete.filter((id) => id !== doc._id),
    }));
  };

  // Clickable field handlers
  const handleEntryClick = (entryNumber: string) => {
    setSearchText(entryNumber);
  };

  const handleTypeClick = (type: CustomsDocumentType) => {
    setFilterType((prev) => (prev === type ? null : type));
  };

  const handleIdentifierClick = (_type: string, value: string) => {
    setSearchText(value);
  };

  // Audit handlers
  const handleRunAudit = async (clusterId: string) => {
    setLoadingStates((prev) => ({ ...prev, audit: [...prev.audit, clusterId] }));
    try {
      const result = await runClusterAudit({
        clusterId,
        tenantId: isTDAdmin && selectedTenant ? selectedTenant._id : undefined,
      }).unwrap();
      if (result) {
        setAuditResults((prev) => ({ ...prev, [clusterId]: result }));
        show(
          "Audit Complete",
          `Audit completed with status: ${getAuditStatusLabel(result.status)}`,
          result.status === "APPROVED" ? "success" : result.status === "NEEDS_REVIEW" ? "warning" : "error"
        );
      }
    } catch (error) {
      show("Audit Failed", (error as Error).message, "error");
    }
    setLoadingStates((prev) => ({
      ...prev,
      audit: prev.audit.filter((id) => id !== clusterId),
    }));
  };

  const handleViewAudit = (audit: ClusterAudit) => {
    setSelectedAudit(audit);
    setShowAuditModal(true);
  };

  // Clear audit results when tenant/client changes to avoid stale data
  useEffect(() => {
    setAuditResults({});
  }, [selectedClient?._id, selectedTenant?._id]);

  // Load audits when clusters change
  useEffect(() => {
    let cancelled = false;

    const fetchAudits = async () => {
      if (!clustersData?.data || clustersData.data.length === 0) return;

      // Clear previous results before fetching new ones
      setAuditResults({});

      for (const cluster of clustersData.data) {
        if (cancelled) return; // Stop if component unmounted or deps changed
        try {
          const result = await getClusterAudit({
            clusterId: cluster._id,
            tenantId: isTDAdmin && selectedTenant ? selectedTenant._id : undefined,
          }).unwrap();
          if (result && !cancelled) {
            setAuditResults((prev) => ({ ...prev, [cluster._id]: result }));
          }
        } catch {
          // Ignore - cluster may not have an audit yet
        }
      }
    };

    fetchAudits();

    return () => {
      cancelled = true;
    };
  }, [clustersData?.data, getClusterAudit, isTDAdmin, selectedTenant]);

  // Debounced search
  const debouncedSearch = useDebounce<string>((value) => {
    setSearchText(value);
  }, 500);

  const clusters = clustersData?.data || [];
  const isLoading = clustersLoading || documentsLoading;

  return (
    <Stack sx={{ flex: 1 }}>
      <PageHeader
        title={"Document Storage"}
        rightNode={
          <HeaderRightNode gap={3}>
          <HeaderSearchBox>
            <StyledTextInput
              type="text"
              placeholder="Search by Entry, BL, Container, PO, or Invoice #..."
              value={searchText}
              handleChangeValue={debouncedSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconImage src={search_img} size="auto" />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchText("");
                        debouncedSearch("");
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </HeaderSearchBox>
            {canUploadDocs && (
              <Box>
                <StyledContainedButton
                  startIcon={<IconImage src={upload_img} size="auto" />}
                  size="large"
                  title="Upload Documents"
                  onClick={() => setShowUploadModal(true)}
                />
              </Box>
            )}
          </HeaderRightNode>
        }
      />

      {/* Filter bar */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E5E7EB" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="body2" sx={{ color: "#6B7280" }}>
            Filter by type:
          </Typography>
          {[
            "ENTRY_SUMMARY_7501",
            "COMMERCIAL_INVOICE",
            "BILL_OF_LADING",
            "PACKING_LIST",
            "OTHER",
          ].map((type) => (
            <Chip
              key={type}
              label={getDocumentTypeLabel(type as CustomsDocumentType)}
              size="small"
              variant={filterType === type ? "filled" : "outlined"}
              color={filterType === type ? "primary" : "default"}
              sx={{ cursor: "pointer" }}
              onClick={() => handleTypeClick(type as CustomsDocumentType)}
            />
          ))}
          {filterType && (
            <Chip
              label="Clear"
              size="small"
              onDelete={() => setFilterType(null)}
              sx={{ ml: 1 }}
            />
          )}
        </Box>
      </Box>

      {/* Processing Banner */}
      {isPolling && processingCounts.processing > 0 && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: "#EFF6FF",
            borderBottom: "1px solid #BFDBFE",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CircularProgress size={18} sx={{ color: "#2563EB" }} />
          <Typography variant="body2" sx={{ color: "#1E40AF" }}>
            Processing {processingCounts.processing} document
            {processingCounts.processing !== 1 ? "s" : ""}...
            {processingCounts.ready > 0 && (
              <Box component="span" sx={{ ml: 1, color: "#059669" }}>
                {processingCounts.ready} ready
              </Box>
            )}
            {processingCounts.failed > 0 && (
              <Box component="span" sx={{ ml: 1, color: "#DC2626" }}>
                {processingCounts.failed} failed
              </Box>
            )}
          </Typography>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
        {shouldSkip ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <Typography variant="body1" sx={{ color: "#6B7280" }}>
              Please select a client to view documents
            </Typography>
          </Box>
        ) : isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : clusters.length === 0 && unclusteredDocs.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
              gap: 2,
            }}
          >
            <Folder sx={{ fontSize: 48, color: "#D1D5DB" }} />
            <Typography variant="body1" sx={{ color: "#6B7280" }}>
              No documents found. Upload documents to get started.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Summary */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                {clusters.length} entry cluster{clusters.length !== 1 ? "s" : ""} ·{" "}
                {unclusteredDocs.length} unclustered document{unclusteredDocs.length !== 1 ? "s" : ""}
              </Typography>
            </Box>

            {/* Clusters */}
            {clusters.map((cluster) => {
              const docs = clusteredDocs[cluster._id] || [];
              // Skip empty clusters (after filtering)
              if (filterType && docs.length === 0) return null;

              return (
                <ClusterRow
                  key={cluster._id}
                  cluster={cluster}
                  documents={docs}
                  isExpanded={expandedClusters.has(cluster._id)}
                  onToggle={() => toggleCluster(cluster._id)}
                  onDownload={handleDownload}
                  onDelete={handleDelete}
                  loadingStates={loadingStates}
                  onEntryClick={handleEntryClick}
                  onTypeClick={handleTypeClick}
                  onIdentifierClick={handleIdentifierClick}
                  onRunAudit={handleRunAudit}
                  audit={auditResults[cluster._id] || null}
                  onViewAudit={handleViewAudit}
                />
              );
            })}

            {/* Unclustered Documents */}
            <UnclusteredRow
              documents={unclusteredDocs}
              isExpanded={showUnclustered}
              onToggle={() => setShowUnclustered((prev) => !prev)}
              onDownload={handleDownload}
              onDelete={handleDelete}
              loadingStates={loadingStates}
              onTypeClick={handleTypeClick}
            />
          </>
        )}
      </Box>

      {/* Upload Modal */}
      <UploadCustomsDocuments
        show={showUploadModal}
        setShow={setShowUploadModal}
        onUploadComplete={() => {
          refetchClusters();
          refetchDocuments();
        }}
        tenantId={isTDAdmin && selectedTenant ? selectedTenant._id : undefined}
        clientId={selectedClient?._id}
      />

      {/* Audit Detail Modal */}
      <Dialog
        open={showAuditModal}
        onClose={() => setShowAuditModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px", maxHeight: "90vh" },
        }}
      >
        {selectedAudit && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #E5E7EB",
                pb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Audit Results
                </Typography>
                <Chip
                  icon={
                    selectedAudit.status === "APPROVED" ? (
                      <VerifiedUser sx={{ fontSize: 16 }} />
                    ) : selectedAudit.status === "NEEDS_REVIEW" ? (
                      <Warning sx={{ fontSize: 16 }} />
                    ) : (
                      <ErrorIcon sx={{ fontSize: 16 }} />
                    )
                  }
                  label={getAuditStatusLabel(selectedAudit.status)}
                  color={getAuditStatusColor(selectedAudit.status)}
                  size="small"
                />
                {selectedAudit.confidenceScore !== undefined && (
                  <Chip
                    label={`${selectedAudit.confidenceScore}% confidence`}
                    size="small"
                    sx={{ bgcolor: "#F3F4F6" }}
                  />
                )}
              </Box>
              <IconButton onClick={() => setShowAuditModal(false)} size="small">
                <Close />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
              {/* Entry Info */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ color: "#6B7280", mb: 0.5 }}>
                  Entry Number
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedAudit.entryNumber || "N/A"}
                </Typography>
              </Box>

              {/* Data Completeness Warning */}
              {selectedAudit.dataCompleteness === "LIMITED" && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Limited Analysis:</strong> No ACE trade data was found for this entry.
                    The audit is based on document cross-referencing only.
                  </Typography>
                </Alert>
              )}

              {/* Metadata Row */}
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  mb: 3,
                  p: 2,
                  bgcolor: "#F9FAFB",
                  borderRadius: "8px",
                }}
              >
                <Box>
                  <Typography variant="caption" sx={{ color: "#6B7280" }}>
                    Documents Analyzed
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedAudit.documentsAnalyzed || 0}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#6B7280" }}>
                    Trade Data Lines
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedAudit.tradeDataLinesAnalyzed || 0}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#6B7280" }}>
                    Processing Time
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedAudit.processingTimeMs
                      ? `${(selectedAudit.processingTimeMs / 1000).toFixed(1)}s`
                      : "N/A"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#6B7280" }}>
                    Model
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedAudit.modelUsed || "N/A"}
                  </Typography>
                </Box>
              </Box>

              {/* Discrepancies */}
              {selectedAudit.discrepancies && selectedAudit.discrepancies.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Discrepancies ({selectedAudit.discrepancies.length})
                  </Typography>
                  {selectedAudit.discrepancies.map((discrepancy, idx) => (
                    <Accordion
                      key={discrepancy.id || idx}
                      defaultExpanded={idx === 0}
                      sx={{
                        border: `1px solid ${getSeverityColor(discrepancy.severity)}30`,
                        borderRadius: "8px !important",
                        mb: 1,
                        "&:before": { display: "none" },
                        boxShadow: "none",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{
                          bgcolor: `${getSeverityColor(discrepancy.severity)}08`,
                          borderRadius: "8px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                          {getSeverityIcon(discrepancy.severity)}
                          <Chip
                            label={discrepancy.severity}
                            size="small"
                            sx={{
                              bgcolor: `${getSeverityColor(discrepancy.severity)}20`,
                              color: getSeverityColor(discrepancy.severity),
                              fontWeight: 600,
                              fontSize: "10px",
                              height: "20px",
                            }}
                          />
                          <Chip
                            label={getCategoryLabel(discrepancy.category)}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "10px", height: "20px" }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                            {discrepancy.title}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 2 }}>
                        {/* Description */}
                        <Typography variant="body2" sx={{ mb: 2, color: "#374151" }}>
                          {discrepancy.description}
                        </Typography>

                        {/* Citations */}
                        {discrepancy.citations && discrepancy.citations.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="caption"
                              sx={{ color: "#6B7280", fontWeight: 600, mb: 1, display: "block" }}
                            >
                              Sources:
                            </Typography>
                            {discrepancy.citations.map((citation, cidx) => (
                              <Box
                                key={cidx}
                                sx={{
                                  p: 1.5,
                                  mb: 1,
                                  bgcolor: "#F9FAFB",
                                  borderRadius: "6px",
                                  borderLeft: "3px solid #0B494B",
                                }}
                              >
                                <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                                  <Chip
                                    label={citation.documentName || "Document"}
                                    size="small"
                                    sx={{ fontSize: "10px", height: "18px" }}
                                  />
                                  {citation.documentType && (
                                    <Chip
                                      label={getDocumentTypeLabel(citation.documentType as CustomsDocumentType)}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: "10px", height: "18px" }}
                                    />
                                  )}
                                  {citation.tradeDataLine && (
                                    <Chip
                                      label={`Line: ${citation.tradeDataLine}`}
                                      size="small"
                                      sx={{ fontSize: "10px", height: "18px", bgcolor: "#E0E7FF", color: "#3730A3" }}
                                    />
                                  )}
                                </Box>
                                {citation.excerpt && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "block",
                                      color: "#4B5563",
                                      fontStyle: "italic",
                                      mt: 0.5,
                                    }}
                                  >
                                    "{citation.excerpt}"
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        )}

                        {/* Recommendation */}
                        {discrepancy.recommendation && (
                          <Box
                            sx={{
                              p: 1.5,
                              bgcolor: "#ECFDF5",
                              borderRadius: "6px",
                              border: "1px solid #A7F3D0",
                            }}
                          >
                            <Typography variant="caption" sx={{ color: "#065F46", fontWeight: 600 }}>
                              Recommendation:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#047857", mt: 0.5 }}>
                              {discrepancy.recommendation}
                            </Typography>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              ) : (
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    No discrepancies found. All documents appear to be consistent with the trade data.
                  </Typography>
                </Alert>
              )}

              {/* Analysis Text */}
              {selectedAudit.analysisText && (
                <Accordion
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px !important",
                    mb: 2,
                    "&:before": { display: "none" },
                    boxShadow: "none",
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Full Analysis
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "pre-wrap", color: "#374151", lineHeight: 1.7 }}
                    >
                      {selectedAudit.analysisText}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Thinking Trail */}
              {selectedAudit.thinkingTrail && selectedAudit.thinkingTrail.length > 0 && (
                <Accordion
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px !important",
                    "&:before": { display: "none" },
                    boxShadow: "none",
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Reasoning Steps ({selectedAudit.thinkingTrail.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box component="ol" sx={{ pl: 2, m: 0 }}>
                      {selectedAudit.thinkingTrail.map((step, idx) => (
                        <Box
                          component="li"
                          key={idx}
                          sx={{ mb: 1, color: "#4B5563", fontSize: "13px" }}
                        >
                          {step}
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: "1px solid #E5E7EB" }}>
              <Button onClick={() => setShowAuditModal(false)} variant="outlined">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Stack>
  );
};

export default EntrySummary;
